import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from 'framer-motion';
import { ArrowRight, Check, Phone, MessageCircle, Globe, Star } from 'lucide-react';
import Seo from '../components/Seo';
import Reveal, { MaskedLines } from '../components/Reveal';
import Marquee from '../components/Marquee';
import SectionHeading from '../components/SectionHeading';
import PackageCard from '../components/PackageCard';
import FaqAccordion from '../components/FaqAccordion';
import ArrowLink from '../components/ArrowLink';
import { LogoMark } from '../components/Logo';
import { IMAGES, TRADES, TRADE_PAGES, PACKAGES, MONTHLY_PLANS, SERVICES, PROBLEMS, TRUST_POINTS, FAQS } from '../data/content';

const EASE = [0.16, 1, 0.3, 1];

function HeroVisual() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [5, -5]), { stiffness: 120, damping: 20 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-6, 6]), { stiffness: 120, damping: 20 });
  const floatX = useTransform(mx, [0, 1], [-10, 10]);
  const floatY = useTransform(my, [0, 1], [-8, 8]);
  const floatX2 = useTransform(mx, [0, 1], [14, -14]);
  const floatY2 = useTransform(my, [0, 1], [10, -10]);

  const onMove = (e) => {
    if (reduce || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => {
        mx.set(0.5);
        my.set(0.5);
      }}
      className="relative mx-auto w-full max-w-md [perspective:1400px] lg:max-w-none"
      data-testid="hero-visual"
    >
      <motion.div
        initial={reduce ? false : { opacity: 0, y: 50, rotateX: 10 }}
        animate={reduce ? undefined : { opacity: 1, y: 0, rotateX: 0 }}
        transition={{ duration: 0.9, delay: 0.5, ease: EASE }}
        style={reduce ? undefined : { rotateX, rotateY, transformStyle: 'preserve-3d' }}
        className="relative"
      >
        {/* Website mock */}
        <div className="overflow-hidden rounded-2xl border border-charcoal-line bg-white shadow-[0_40px_80px_-32px_rgba(0,0,0,0.55)]">
          <div className="flex items-center gap-2 border-b border-hairline bg-offwhite-light px-4 py-3">
            <span className="h-2.5 w-2.5 rounded-full bg-charcoal/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-charcoal/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-charcoal/15" />
            <span className="ml-3 flex-1 rounded-full bg-charcoal/5 px-3 py-1 font-mono text-[10px] text-stonebrand">
              yourbusiness.co.uk
            </span>
          </div>
          <div className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <LogoMark className="h-5 w-5 text-charcoal" />
                <span className="font-heading text-xs font-extrabold text-charcoal">YOUR BUSINESS</span>
              </div>
              <span className="rounded-full bg-lime px-3 py-1 font-heading text-[10px] font-bold text-charcoal">
                Get a Quote
              </span>
            </div>
            <div className="mt-5 space-y-2">
              <div className="h-3.5 w-4/5 rounded-full bg-charcoal/80" />
              <div className="h-3.5 w-3/5 rounded-full bg-charcoal/25" />
            </div>
            <img
              src={IMAGES.workshop.url}
              alt={IMAGES.workshop.alt}
              loading="eager"
              className="mt-5 h-32 w-full rounded-xl object-cover sm:h-40"
            />
            <div className="mt-4 flex gap-2">
              <span className="flex-1 rounded-lg bg-charcoal py-2 text-center text-[10px] font-semibold text-offwhite">
                Call now
              </span>
              <span className="flex-1 rounded-lg border border-hairline py-2 text-center text-[10px] font-semibold text-charcoal">
                Our work
              </span>
            </div>
          </div>
        </div>

        {/* Business profile mock */}
        <motion.div
          style={reduce ? undefined : { x: floatX, y: floatY }}
          className="absolute -right-3 -top-8 w-52 rounded-xl border border-hairline bg-white p-4 shadow-[0_24px_50px_-20px_rgba(0,0,0,0.5)] sm:-right-8"
        >
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-charcoal">
              <LogoMark className="h-5 w-5 text-offwhite" />
            </span>
            <div>
              <p className="font-heading text-xs font-bold text-charcoal">Your Business</p>
              <p className="text-[10px] text-stonebrand">Your trade · Your area</p>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-3 w-3 fill-charcoal text-charcoal" aria-hidden="true" />
            ))}
            <span className="ml-1 text-[10px] font-semibold text-charcoal">Your reviews</span>
          </div>
          <div className="mt-3 flex gap-1.5">
            {[Phone, MessageCircle, Globe].map((Icon, i) => (
              <span key={i} className="flex h-7 flex-1 items-center justify-center rounded-md bg-offwhite">
                <Icon className="h-3.5 w-3.5 text-charcoal" aria-hidden="true" />
              </span>
            ))}
          </div>
        </motion.div>

        {/* Social mock */}
        <motion.div
          style={reduce ? undefined : { x: floatX2, y: floatY2 }}
          className="absolute -bottom-10 -left-3 w-48 rounded-xl border border-charcoal-line bg-charcoal-surface p-3.5 shadow-[0_24px_50px_-20px_rgba(0,0,0,0.6)] sm:-left-10"
        >
          <div className="flex items-center gap-2">
            <span className="h-7 w-7 rounded-full bg-lime" />
            <div>
              <p className="font-heading text-[11px] font-bold text-offwhite">yourbusiness</p>
              <p className="text-[9px] text-offwhite/50">Latest work</p>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-1.5">
            {[IMAGES.plumbingDetail, IMAGES.electrical, IMAGES.carpentry].map((img, i) => (
              <img key={i} src={img.url} alt="" aria-hidden="true" className="h-12 w-full rounded-md object-cover" />
            ))}
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default function HomePage() {
  const faqPreview = FAQS.flatMap((c) => c.items).slice(0, 6);
  return (
    <>
      <Seo
        title="Build My Trade — Websites & Online Profiles for UK Tradespeople"
        description="We build professional websites, Google Business Profiles and social profiles for tradespeople. Straightforward packages from £249. Clear prices, no marketing jargon."
        path="/"
        image={IMAGES.workshop.url}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-charcoal bg-blueprint" data-testid="hero-section">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-4 pb-28 pt-32 sm:px-6 sm:pt-40 lg:grid-cols-2 lg:gap-12 lg:px-8 lg:pb-36">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="inline-flex items-center gap-2 rounded-full border border-charcoal-line bg-charcoal-surface px-4 py-2 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-lime"
              data-testid="hero-eyebrow"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-lime" aria-hidden="true" />
              Websites & profiles for UK tradespeople
            </motion.p>
            <MaskedLines
              lines={['Your trade business', { text: 'deserves to be found.', accent: true }]}
              testid="hero-headline"
              className="mt-7 text-balance font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-offwhite sm:text-5xl lg:text-6xl"
            />
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55, ease: EASE }}
              className="mt-6 max-w-xl text-base leading-relaxed text-offwhite/70 sm:text-lg"
              data-testid="hero-subcopy"
            >
              We build professional websites and online profiles for tradespeople who want to look more established,
              reach more local customers and make it easier for people to get in touch.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7, ease: EASE }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <ArrowLink to="/pricing" variant="lime" testid="hero-cta-packages" className="px-7 py-3.5 text-base">
                Choose Your Package
              </ArrowLink>
              <ArrowLink to="/contact" variant="outline-light" testid="hero-cta-quote" className="px-7 py-3.5 text-base">
                Get a Free Quote
              </ArrowLink>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="mt-7 text-sm text-offwhite/50"
              data-testid="hero-reassurance"
            >
              Straightforward packages. Clear prices. No marketing jargon.
            </motion.p>
          </div>
          <div className="pb-10 lg:pb-0">
            <HeroVisual />
          </div>
        </div>
      </section>

      <Marquee items={TRADES} />

      {/* Problem / solution */}
      <section className="bg-offwhite" data-testid="problem-section">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-32">
          <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
            <div>
              <SectionHeading
                eyebrow="The problem"
                title="Brilliant at your trade. Not sure where to start online?"
                intro="You’re not behind because you’re bad at business — you’re busy doing the actual work. Meanwhile, the way customers find tradespeople has moved online."
                testid="problem-heading"
              />
              <Reveal delay={0.15} className="mt-10">
                <div className="relative overflow-hidden rounded-2xl">
                  <img
                    src={IMAGES.carpentry.url}
                    alt={IMAGES.carpentry.alt}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover"
                  />
                  <p className="absolute bottom-0 left-0 m-4 rounded-full bg-charcoal/90 px-4 py-2 text-xs font-medium text-offwhite backdrop-blur">
                    Real work deserves a real online presence
                  </p>
                </div>
              </Reveal>
            </div>
            <div className="flex flex-col justify-center">
              <ul className="space-y-4">
                {PROBLEMS.map((p, i) => (
                  <Reveal key={i} delay={0.08 * i}>
                    <li className="flex gap-4 rounded-2xl border border-hairline bg-white p-5 transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-charcoal/25">
                      <span className="mt-1 h-2.5 w-2.5 shrink-0 bg-lime ring-1 ring-charcoal/20" aria-hidden="true" />
                      <div>
                        <h3 className="font-heading text-base font-bold tracking-tight text-charcoal">{p.title}</h3>
                        <p className="mt-1 text-sm leading-relaxed text-stonebrand">{p.text}</p>
                      </div>
                    </li>
                  </Reveal>
                ))}
              </ul>
              <Reveal delay={0.3}>
                <div className="mt-8 rounded-2xl bg-charcoal p-6 text-offwhite">
                  <p className="font-heading text-lg font-bold tracking-tight">
                    That’s the whole reason Build My Trade exists.
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-offwhite/70">
                    One straightforward, managed setup: we build your website, Google profile and social pages, and
                    hand everything over ready to run. You keep doing the trade — we handle the online part.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Services overview */}
      <section className="border-y border-hairline bg-white" data-testid="services-overview">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-32">
          <SectionHeading
            eyebrow="What we do"
            title="Everything your trade business needs online, in one place"
            intro="Six services, one point of contact, zero jargon. Each one is included in a package or available as a simple monthly plan."
            testid="services-heading"
          />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={0.06 * i} className="h-full">
                <Link
                  to={`/services#${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="group flex h-full flex-col rounded-2xl border border-hairline bg-offwhite-light p-7 transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:border-charcoal/30 hover:shadow-[0_20px_45px_-28px_rgba(36,39,33,0.3)]"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-charcoal transition-colors duration-300 group-hover:bg-lime">
                    <s.icon className="h-5 w-5 text-offwhite transition-colors duration-300 group-hover:text-charcoal" aria-hidden="true" />
                  </span>
                  <h3 className="mt-6 font-heading text-lg font-bold tracking-tight text-charcoal">{s.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-stonebrand">{s.summary}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-charcoal">
                    Learn more
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Who we help — trade landing pages */}
      <section className="border-b border-hairline bg-offwhite bg-blueprint-light" data-testid="who-we-help">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-32">
          <SectionHeading
            eyebrow="Who we help"
            title="Whatever your trade, we speak your language"
            intro="Every trade wins work differently — an emergency plumber and a bespoke joiner need different things from a website. See exactly what we build for yours."
            testid="who-we-help-heading"
          />
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {TRADE_PAGES.map((t, i) => (
              <Reveal key={t.slug} delay={0.05 * i} className="h-full">
                <Link
                  to={`/${t.slug}`}
                  data-testid={`trade-link-${t.slug}`}
                  className="group flex h-full items-center justify-between gap-4 rounded-2xl border border-hairline bg-white p-6 transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:border-charcoal/30 hover:shadow-[0_20px_45px_-28px_rgba(36,39,33,0.3)]"
                >
                  <span>
                    <span className="font-heading text-lg font-bold tracking-tight text-charcoal">{t.label}</span>
                    <span className="mt-1 block text-sm leading-relaxed text-stonebrand">{t.line}</span>
                  </span>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-charcoal transition-colors duration-300 group-hover:bg-lime">
                    <ArrowRight
                      className="h-4 w-4 text-offwhite transition-[color,transform] duration-300 group-hover:translate-x-0.5 group-hover:text-charcoal"
                      aria-hidden="true"
                    />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-offwhite bg-blueprint-light" data-testid="how-it-works">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-32">
          <SectionHeading
            eyebrow="How it works"
            title="Online in three straightforward steps"
            testid="how-it-works-heading"
          />
          <div className="mt-14 grid gap-5 lg:grid-cols-3">
            {[
              {
                n: '01',
                title: 'Choose your package',
                text: 'Pick the package that fits where your business is right now — or send an enquiry and we’ll help you decide. No obligation either way.',
              },
              {
                n: '02',
                title: 'Tell us about your trade',
                text: 'A short, plain-English questionnaire about your services, your area and your work. Send some photos from your phone — that’s genuinely all we need.',
              },
              {
                n: '03',
                title: 'We build, you approve',
                text: 'We build your website, set up your profiles and prepare everything for launch. You review it, we refine it, and then it goes live.',
              },
            ].map((step, i) => (
              <Reveal key={step.n} delay={0.1 * i} className="h-full">
                <div className="relative h-full rounded-2xl border border-hairline bg-white p-8">
                  <span className="font-mono text-xs font-semibold tracking-[0.2em] text-stonebrand">{step.n}</span>
                  <h3 className="mt-4 font-heading text-xl font-bold tracking-tight text-charcoal">{step.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-stonebrand">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-3xl text-xs leading-relaxed text-stonebrand">
              Timings depend on how quickly information comes back, and on things outside anyone’s control — Google
              verification, domain registration and platform approvals. We’ll always tell you exactly where things
              stand.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Pricing preview */}
      <section className="bg-charcoal" data-testid="pricing-preview">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-32">
          <SectionHeading
            dark
            eyebrow="One-off packages"
            title="Fixed prices. Everything listed. Nothing hidden."
            intro="Three packages, one-off payments, published prices. Pick the one that matches where your business is — monthly support is optional, never forced."
            testid="pricing-preview-heading"
          />
          <div className="mt-16 grid items-stretch gap-6 lg:grid-cols-3">
            {PACKAGES.map((pkg, i) => (
              <PackageCard key={pkg.slug} pkg={pkg} delay={0.08 * i} />
            ))}
          </div>
          <Reveal delay={0.2} className="mt-10 text-center">
            <Link
              to="/pricing"
              data-testid="pricing-preview-link"
              className="group inline-flex items-center gap-2 font-heading text-sm font-bold text-lime"
            >
              Compare packages and monthly plans in full
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Monthly plan spotlight */}
      <section className="border-b border-hairline bg-offwhite" data-testid="monthly-spotlight">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-32">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <SectionHeading
                eyebrow="Optional monthly support"
                title="Already online? We’ll keep it that way."
                intro="Three rolling care plans for customers who’d rather spend evenings anywhere than updating a website or writing social posts. Completely optional — your one-off package works perfectly well on its own."
                testid="monthly-spotlight-heading"
              />
              <Reveal delay={0.15}>
                <p className="mt-6 rounded-xl border border-hairline bg-white px-5 py-4 text-sm leading-relaxed text-stonebrand">
                  <span className="font-semibold text-charcoal">No contracts, no pressure.</span> Cancel before your
                  next billing date and nothing more is taken.
                </p>
              </Reveal>
            </div>
            <div className="grid gap-5 sm:grid-cols-3 lg:col-span-8">
              {MONTHLY_PLANS.map((plan, i) => (
                <Reveal key={plan.slug} delay={0.08 * i} className="h-full">
                  <div className="flex h-full flex-col rounded-2xl border border-hairline bg-white p-7 transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:border-charcoal/30 hover:shadow-[0_20px_45px_-28px_rgba(36,39,33,0.3)]">
                    <h3 className="font-heading text-lg font-bold tracking-tight text-charcoal">{plan.name}</h3>
                    <p className="mt-4 flex items-baseline gap-1.5">
                      <span
                        className="font-heading text-3xl font-extrabold tracking-tight text-charcoal"
                        data-testid={`monthly-price-${plan.slug}`}
                      >
                        {plan.price}
                      </span>
                      <span className="text-sm text-stonebrand">{plan.period}</span>
                    </p>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-stonebrand">{plan.description}</p>
                    <Link
                      to={`/contact?package=${plan.slug}`}
                      data-testid={`monthly-cta-${plan.slug}`}
                      className="group mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-charcoal"
                    >
                      Request this plan
                      <ArrowRight
                        className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                        aria-hidden="true"
                      />
                    </Link>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.2} className="mt-10">
            <Link
              to="/pricing"
              data-testid="monthly-spotlight-link"
              className="group inline-flex items-center gap-2 font-heading text-sm font-bold text-charcoal"
            >
              See exactly what each plan includes
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Trust */}
      <section className="bg-offwhite" data-testid="trust-section">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-32">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionHeading
                eyebrow="Why Build My Trade"
                title="You do the trade. We build the business."
                intro="No inflated promises, no lock-in contracts and no invented five-star reviews. Just a straight service that does what it says on the van."
                testid="trust-heading"
              />
              <Reveal delay={0.15} className="mt-10">
                <img
                  src={IMAGES.plumbingHero.url}
                  alt={IMAGES.plumbingHero.alt}
                  loading="lazy"
                  className="aspect-[4/5] w-full rounded-2xl object-cover"
                />
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <ul className="grid gap-5 sm:grid-cols-2">
                {TRUST_POINTS.map((t, i) => (
                  <Reveal key={i} delay={0.07 * i} className="h-full">
                    <li className="flex h-full flex-col rounded-2xl border border-hairline bg-white p-7">
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-lime">
                        <Check className="h-5 w-5 text-charcoal" aria-hidden="true" />
                      </span>
                      <h3 className="mt-5 font-heading text-lg font-bold tracking-tight text-charcoal">{t.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-stonebrand">{t.text}</p>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ preview */}
      <section className="border-y border-hairline bg-white" data-testid="faq-preview">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-32">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <SectionHeading
                eyebrow="Common questions"
                title="Fair questions, straight answers"
                intro="Pricing, timings, ownership, Google — the things every tradesperson asks before getting a website."
                testid="faq-preview-heading"
              />
              <Reveal delay={0.15}>
                <ArrowLink to="/faqs" variant="outline-dark" testid="faq-preview-link" className="mt-8">
                  Read all FAQs
                </ArrowLink>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              <Reveal delay={0.1}>
                <FaqAccordion items={faqPreview} testid="home-faq" />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-charcoal-deep bg-blueprint" data-testid="final-cta">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-36">
          <div className="mx-auto max-w-3xl text-center">
            <Reveal>
              <h2 className="text-balance font-heading text-3xl font-extrabold leading-[1.1] tracking-tight text-offwhite sm:text-4xl lg:text-5xl">
                Ready to get your trade business <span className="text-lime">online?</span>
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-offwhite/65 sm:text-lg">
                Tell us about your business and we’ll help you choose the right starting point.
              </p>
            </Reveal>
            <Reveal delay={0.22}>
              <div className="mt-10">
                <ArrowLink to="/contact" variant="lime" testid="final-cta-quote" className="px-8 py-4 text-base">
                  Get Your Free Quote
                </ArrowLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
